/**
 * Meta (Facebook) Pixel loader and helper.
 * Initializes the pixel when REACT_APP_META_PIXEL_ID is set.
 */

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID

export function initMetaPixel() {
  if (!PIXEL_ID) return

  // Make initialization idempotent. In SPAs (and during HMR) this module can be executed more than once.
  // Also, some setups load the base pixel via GTM; in that case we should not clobber an existing fbq.
  if (window.__metaPixelInitialized) return

  if (typeof window.fbq !== 'function') {
    const f = function () {
      f.callMethod ? f.callMethod.apply(f, arguments) : f.queue.push(arguments)
    }
    window.fbq = f
    f.push = f
    f.loaded = true
    f.version = '2.0'
    f.queue = []
  }

  const pixelSrc = 'https://connect.facebook.net/en_US/fbevents.js'
  const alreadyHasScript = Array.from(document.getElementsByTagName('script')).some(
    (el) => el?.src === pixelSrc
  )
  if (!alreadyHasScript) {
    const s = document.createElement('script')
    s.async = true
    s.src = pixelSrc
    const first = document.getElementsByTagName('script')[0]
    first?.parentNode?.insertBefore(s, first)
  }

  window.fbq('init', PIXEL_ID)
  window.fbq('track', 'PageView')
  window.__metaPixelInitialized = true
}

/**
 * Fire a Meta Pixel event (e.g. Purchase, AddToCart).
 * Safe to call even if pixel is not loaded.
 * @param {string} eventName - Standard event: PageView, ViewContent, AddToCart, InitiateCheckout, Purchase, etc.
 * @param {object} [params] - Optional event parameters (value, currency, and optional user: ph, fn, ln, external_id)
 */
export function trackMetaPixelEvent(eventName, params) {
  if (typeof window.fbq !== 'function') return
  if (params) {
    window.fbq('track', eventName, params)
  } else {
    window.fbq('track', eventName)
  }
}

/**
 * Build user params for Meta Advanced Matching.
 * Uses Meta's keys: ph (phone), fn (first name), ln (last name), external_id.
 * Must be passed to fbq('init', pixelId, userData) to be used; track() params alone are not used for matching.
 * @param {object} opts - { phone, firstName, lastName, externalId }
 * @returns {object} User data object for fbq('init', pixelId, userData)
 */
export function metaPixelUserParams(opts) {
  const p = {}
  if (opts?.phone) p.ph = String(opts.phone).replace(/\D/g, '').slice(-10)
  if (opts?.firstName?.trim()) p.fn = opts.firstName.trim().toLowerCase()
  if (opts?.lastName?.trim()) p.ln = opts.lastName.trim().toLowerCase()
  if (opts?.externalId) p.external_id = String(opts.externalId)
  return p
}

/**
 * Update Meta Pixel with user data for Advanced Matching.
 * Call this before firing events (e.g. Purchase) so the next event is matched to this user.
 * Meta only uses user data from fbq('init', pixelId, userData), not from track() params.
 */
export function setMetaPixelUserData(userData) {
  if (!PIXEL_ID || typeof window.fbq !== 'function') return
  const data = Object.keys(userData).length ? userData : undefined
  window.fbq('init', PIXEL_ID, data)
}
